import {
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Info } from 'lucide-react';
import {
  termsApi,
  TERMS_TYPE_LABEL,
  type TermsCreateInput,
  type TermsTargetRole,
  type TermsType,
  type TermsUpdateInput,
} from '@/services/api';
import {
  PageHeader,
  Section,
  TextInput,
  TextArea,
  Select,
  LoadingState,
  ErrorState,
  RichTextEditor,
} from '@/components/common';
import { sanitizeTermsHtml } from '@/utils/sanitizeHtml';

interface FormState {
  targetRole: TermsTargetRole;
  termsType: TermsType;
  termsTitle: string;
  termsVersion: string;
  /** 본문 HTML (백엔드 키와 동일) */
  contentHtml: string;
  termsUrl: string;
  changeSummary: string;
  isRequired: boolean;
  /** 시행일 — 일자 비어있으면 임시저장(DRAFT) */
  effectiveDate: string;
  effectiveHour: string;
  effectiveMinute: string;
  /** 고지 시작일 — 비어있으면 미설정 */
  noticeDate: string;
  noticeHour: string;
  noticeMinute: string;
}

const DEFAULT_HOUR = '00';
const DEFAULT_MINUTE = '00';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
);
const MINUTE_OPTIONS = ['00', '10', '20', '30', '40', '50'];

const TARGET_OPTIONS: { value: TermsTargetRole; label: string; help: string }[] = [
  { value: 'GUARDIAN', label: '보호자', help: '보호자앱 가입·조회 시 노출' },
  { value: 'BUSINESS', label: '사업자', help: '대표·임직원 가입 시 노출' },
  { value: 'COMMON', label: '공통', help: '전용 약관이 없을 때 폴백으로 사용' },
];

const TYPE_OPTIONS: TermsType[] = [
  'TERMS_TYPE_SERVICE',
  'TERMS_TYPE_PRIVACY',
  'TERMS_TYPE_LOCATION',
  'TERMS_TYPE_MARKETING',
];

function splitDateTime(value: string | null | undefined): {
  date: string;
  hour: string;
  minute: string;
} {
  if (!value) return { date: '', hour: '', minute: '' };
  const [datePart = '', timePart = ''] = value.replace('T', ' ').split(' ');
  const [hh = '', mm = ''] = timePart.split(':');
  return { date: datePart, hour: hh, minute: mm };
}

function joinDateTime(
  date: string,
  hour: string,
  minute: string,
): string | undefined {
  if (!date) return undefined;
  const hh = (hour || DEFAULT_HOUR).padStart(2, '0');
  const mm = (minute || DEFAULT_MINUTE).padStart(2, '0');
  return `${date} ${hh}:${mm}:00`;
}

/** v1.2 → v1.3 처럼 마지막 숫자만 올린다. 규칙에 안 맞으면 그대로 둔다. */
function suggestNextVersion(version: string): string {
  const match = version.match(/^(.*?)(\d+)$/);
  if (!match) return version;
  return `${match[1]}${Number(match[2]) + 1}`;
}

const emptyForm: FormState = {
  targetRole: 'GUARDIAN',
  termsType: 'TERMS_TYPE_SERVICE',
  termsTitle: '',
  termsVersion: 'v1.0',
  contentHtml: '',
  termsUrl: '',
  changeSummary: '',
  isRequired: true,
  effectiveDate: '',
  effectiveHour: DEFAULT_HOUR,
  effectiveMinute: DEFAULT_MINUTE,
  noticeDate: '',
  noticeHour: DEFAULT_HOUR,
  noticeMinute: DEFAULT_MINUTE,
};

interface Props {
  mode?: 'create' | 'edit';
}

export function TermsEditorPage({ mode = 'create' }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();

  const editingId = mode === 'edit' && params.id ? Number(params.id) : null;
  const isEdit = editingId != null;

  /** 새 버전 등록 시 원본으로 삼을 기존 버전 (?from=seq) */
  const fromId = !isEdit && searchParams.get('from')
    ? Number(searchParams.get('from'))
    : null;

  const sourceId = isEdit ? editingId : fromId;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: source,
    isLoading: isLoadingSource,
    error: sourceError,
    refetch,
  } = useQuery({
    queryKey: ['terms', sourceId],
    queryFn: () => termsApi.getTermsDetail(sourceId as number),
    enabled: sourceId != null,
  });

  useEffect(() => {
    if (!source) return;

    if (isEdit) {
      const eff = splitDateTime(source.effectiveAt);
      const notice = splitDateTime(source.noticeAt);
      setForm({
        targetRole: source.targetRole,
        termsType: source.termsType,
        termsTitle: source.termsTitle,
        termsVersion: source.termsVersion,
        contentHtml: source.contentHtml ?? '',
        termsUrl: source.termsUrl ?? '',
        changeSummary: source.changeSummary ?? '',
        isRequired: source.isRequired,
        effectiveDate: eff.date,
        effectiveHour: eff.hour || DEFAULT_HOUR,
        effectiveMinute: eff.minute || DEFAULT_MINUTE,
        noticeDate: notice.date,
        noticeHour: notice.hour || DEFAULT_HOUR,
        noticeMinute: notice.minute || DEFAULT_MINUTE,
      });
      return;
    }

    // 새 버전: 본문·대상·유형은 이어받고 시행일/고지일/변경요약은 비운다
    setForm({
      ...emptyForm,
      targetRole: source.targetRole,
      termsType: source.termsType,
      termsTitle: source.termsTitle,
      termsVersion: suggestNextVersion(source.termsVersion),
      contentHtml: source.contentHtml ?? '',
      termsUrl: source.termsUrl ?? '',
      isRequired: source.isRequired,
    });
  }, [isEdit, source]);

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof FormState];
        return next;
      });
    }
  };

  const stripHtml = (html: string): string =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const buildEffectiveAt = (): string | undefined =>
    joinDateTime(form.effectiveDate, form.effectiveHour, form.effectiveMinute);
  const buildNoticeAt = (): string | undefined =>
    joinDateTime(form.noticeDate, form.noticeHour, form.noticeMinute);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!form.termsTitle.trim()) next.termsTitle = '약관 제목을 입력해주세요.';
    if (!form.termsVersion.trim()) next.termsVersion = '버전을 입력해주세요.';
    if (!stripHtml(form.contentHtml)) {
      next.contentHtml = '약관 본문을 입력해주세요.';
    }
    const effective = buildEffectiveAt();
    const notice = buildNoticeAt();
    if (effective && notice && notice > effective) {
      next.noticeDate = '고지 시작일은 시행일보다 이전이어야 합니다.';
    }
    if (!effective && notice) {
      next.effectiveDate = '고지 시작일을 넣으려면 시행일도 지정해야 합니다.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    const effective = buildEffectiveAt();
    const notice = buildNoticeAt();

    if (!isEdit && effective) {
      const ok = window.confirm(
        `시행일을 ${effective} 로 등록합니다.\n` +
          '시행일이 지나면 이 버전은 수정·삭제할 수 없습니다. 계속할까요?',
      );
      if (!ok) return;
    }

    setIsSubmitting(true);
    try {
      if (isEdit && editingId != null) {
        // 백엔드 명세: effectiveAt/noticeAt 이 undefined 면 "변경 없음". 비우려면 clear* 플래그.
        const payload: TermsUpdateInput = {
          termsTitle: form.termsTitle.trim(),
          termsVersion: form.termsVersion.trim(),
          contentHtml: sanitizeTermsHtml(form.contentHtml),
          termsUrl: form.termsUrl.trim(),
          changeSummary: form.changeSummary.trim(),
          isRequired: form.isRequired,
        };
        if (effective) {
          payload.effectiveAt = effective;
        } else {
          payload.clearEffectiveAt = true;
        }
        if (notice) {
          payload.noticeAt = notice;
        } else {
          payload.clearNoticeAt = true;
        }
        await termsApi.updateTerms(editingId, payload);
        alert('약관이 수정되었습니다.');
        navigate(`/terms/${editingId}`);
      } else {
        const payload: TermsCreateInput = {
          targetRole: form.targetRole,
          termsType: form.termsType,
          termsTitle: form.termsTitle.trim(),
          termsVersion: form.termsVersion.trim(),
          contentHtml: sanitizeTermsHtml(form.contentHtml),
          termsUrl: form.termsUrl.trim() || undefined,
          changeSummary: form.changeSummary.trim() || undefined,
          isRequired: form.isRequired,
          effectiveAt: effective ?? null,
          noticeAt: notice ?? null,
        };
        const created = await termsApi.createTerms(payload);
        alert('약관 새 버전이 등록되었습니다.');
        navigate(`/terms/${created.id}`);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : isEdit
            ? '약관 수정에 실패했습니다.'
            : '약관 등록에 실패했습니다.';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (sourceId != null && isLoadingSource) {
    return <LoadingState />;
  }
  if (sourceId != null && sourceError) {
    return <ErrorState error={sourceError} onRetry={() => refetch()} />;
  }

  // 시행된 버전은 수정 불가 — 서버에서도 막지만 화면에서 먼저 안내한다
  if (isEdit && source && (source.status === 'ACTIVE' || source.status === 'ARCHIVED')) {
    return (
      <div className="space-y-4">
        <PageHeader title="약관 수정" />
        <Section title="수정할 수 없는 버전입니다">
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              이미 시행된 버전({source.termsVersion})은 회원 동의 기록이 가리키는
              원본이라 내용을 바꿀 수 없습니다. 문구를 바꾸려면 새 버전을 등록하세요.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigate(`/terms/new?from=${source.id}`)}
                className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
              >
                이 버전을 복사해 새 버전 만들기
              </button>
              <button
                type="button"
                onClick={() => navigate(`/terms/${source.id}`)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                상세로 이동
              </button>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={isEdit ? '약관 수정' : '약관 새 버전 등록'}
        subtitle={
          isEdit
            ? '아직 시행되지 않은 버전만 수정할 수 있습니다.'
            : fromId
              ? '기존 버전의 본문을 복사해 새 버전을 만듭니다. 기존 버전은 그대로 보존됩니다.'
              : '대상·유형별 약관을 새 버전으로 등록합니다.'
        }
        right={
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft size={16} />
            뒤로가기
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <Section title="약관 정보">
          <div className="space-y-4">
            <Field
              label="적용 대상"
              required
              help={
                isEdit
                  ? '대상은 버전 계보를 결정하므로 수정할 수 없습니다.'
                  : '보호자/사업자 전용 약관이 없으면 공통(COMMON) 약관이 사용됩니다.'
              }
            >
              <div className="flex flex-wrap gap-3">
                {TARGET_OPTIONS.map(opt => {
                  const checked = form.targetRole === opt.value;
                  const locked = isEdit || isSubmitting;
                  return (
                    <label
                      key={opt.value}
                      title={opt.help}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                        checked
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <input
                        type="radio"
                        name="targetRole"
                        value={opt.value}
                        checked={checked}
                        onChange={() =>
                          setForm(prev => ({ ...prev, targetRole: opt.value }))
                        }
                        disabled={locked}
                        className="accent-primary-500"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field label="약관 유형" required>
              <Select
                name="termsType"
                value={form.termsType}
                onChange={handleTextChange}
                disabled={isEdit || isSubmitting}
                className="max-w-xs"
              >
                {TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>
                    {TERMS_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_200px]">
              <Field label="제목" required error={errors.termsTitle}>
                <TextInput
                  name="termsTitle"
                  value={form.termsTitle}
                  onChange={handleTextChange}
                  placeholder="예) 멍투데이 서비스 이용약관"
                  disabled={isSubmitting}
                />
              </Field>
              <Field
                label="버전"
                required
                error={errors.termsVersion}
                help="대상·유형 안에서 중복 불가"
              >
                <TextInput
                  name="termsVersion"
                  value={form.termsVersion}
                  onChange={handleTextChange}
                  placeholder="v2.0"
                  disabled={isSubmitting}
                />
              </Field>
            </div>

            <Field label="필수 동의 여부">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isRequired}
                  onChange={e =>
                    setForm(prev => ({ ...prev, isRequired: e.target.checked }))
                  }
                  disabled={isSubmitting}
                  className="accent-primary-500"
                />
                가입 시 반드시 동의해야 하는 약관
              </label>
            </Field>

            <Field label="본문" required error={errors.contentHtml}>
              <RichTextEditor
                value={form.contentHtml}
                onChange={html => {
                  setForm(prev => ({ ...prev, contentHtml: html }));
                  if (errors.contentHtml) {
                    setErrors(prev => {
                      const next = { ...prev };
                      delete next.contentHtml;
                      return next;
                    });
                  }
                }}
                placeholder="약관 전문을 붙여넣으세요. 조 제목은 소제목(H2/H3)으로 두면 앱에서 목차로 잡기 쉽습니다."
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="원문 파일 URL"
              help="PDF 등 원문 파일을 함께 제공할 때만 입력합니다. (선택)"
            >
              <TextInput
                name="termsUrl"
                value={form.termsUrl}
                onChange={handleTextChange}
                placeholder="https://..."
                disabled={isSubmitting}
              />
            </Field>
          </div>
        </Section>

        <Section title="시행 정보">
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>
                시행일을 비우면 임시저장 상태로 남고 앱에 노출되지 않습니다. 시행일이
                지나면 그 버전은 수정·삭제할 수 없습니다. 불리한 변경은 시행 30일 전,
                일반 변경은 7일 전 고지가 권장됩니다.
              </span>
            </div>

            <Field
              label="시행일"
              error={errors.effectiveDate}
              help="비우면 임시저장(DRAFT)"
            >
              <DateTimeRow
                dateName="effectiveDate"
                hourName="effectiveHour"
                minuteName="effectiveMinute"
                dateValue={form.effectiveDate}
                hourValue={form.effectiveHour}
                minuteValue={form.effectiveMinute}
                onChange={handleTextChange}
                disabled={isSubmitting}
              />
            </Field>

            <Field label="고지 시작일" error={errors.noticeDate}>
              <DateTimeRow
                dateName="noticeDate"
                hourName="noticeHour"
                minuteName="noticeMinute"
                dateValue={form.noticeDate}
                hourValue={form.noticeHour}
                minuteValue={form.noticeMinute}
                onChange={handleTextChange}
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="변경 요약"
              help="개정 시 무엇이 어떻게 바뀌었는지. 앱의 개정 안내 화면에 그대로 노출할 수 있습니다."
            >
              <TextArea
                name="changeSummary"
                value={form.changeSummary}
                onChange={handleTextChange}
                rows={5}
                placeholder={
                  '예)\n제12조(환불) — 환불 신청 기한 7일 → 14일로 연장\n제18조(분쟁해결) — 관할 법원 조항 신설'
                }
                disabled={isSubmitting}
              />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-60"
          >
            {isSubmitting ? '저장 중...' : isEdit ? '수정 저장' : '새 버전 등록'}
          </button>
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: ReactNode;
}
function Field({ label, required, error, help, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}

interface DateTimeRowProps {
  dateName: string;
  hourName: string;
  minuteName: string;
  dateValue: string;
  hourValue: string;
  minuteValue: string;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  disabled?: boolean;
}
function DateTimeRow({
  dateName,
  hourName,
  minuteName,
  dateValue,
  hourValue,
  minuteValue,
  onChange,
  disabled,
}: DateTimeRowProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <TextInput
        type="date"
        name={dateName}
        value={dateValue}
        onChange={onChange}
        disabled={disabled}
        className="sm:w-44"
      />
      <div className="flex items-center gap-1">
        <Select
          name={hourName}
          value={hourValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {HOUR_OPTIONS.map(h => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">시</span>
        <Select
          name={minuteName}
          value={minuteValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {MINUTE_OPTIONS.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">분</span>
      </div>
    </div>
  );
}
